import React, { useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import { withTranslation, useTranslation } from 'react-i18next'
import { restaurants, updateCommission, getQorConfig } from '../apollo'
import CustomLoader from '../components/Loader/CustomLoader'
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  InputAdornment,
  MenuItem,
  Alert
} from '@mui/material'
import PercentIcon from '@mui/icons-material/Percent'
import StorefrontIcon from '@mui/icons-material/Storefront'
import SaveIcon from '@mui/icons-material/Save'
import ManualDeliveryFeeRow, { MANUAL_DELIVERY_FEE_SETTINGS } from '../components/ManualDeliveryFee/ManualDeliveryFeeRow'

const GET_RESTAURANTS = gql`${restaurants}`
const GET_QOR_CONFIG = gql`${getQorConfig}`
const UPDATE_COMMISSION = gql`${updateCommission}`
// The standard third-party orders % from Signup Standards.
const GET_STANDARD_FEES = gql`query CommissionStandardFees { merchantAgreementFees { thirdPartyPercent } }`

// Who keeps the sales tax. Whoever keeps it remits it to the state — the
// customer pays the same tax either way.
const PLATFORM = 'PLATFORM'
const RESTAURANT = 'RESTAURANT'

const Commission = () => {
  const { t } = useTranslation()
  const [notifications, setNotifications] = useState({})
  // Who-keeps-tax is a controlled field (the rate boxes read the DOM), keyed by
  // restaurant id. Absent = whatever the restaurant is saved with.
  const [taxOwners, setTaxOwners] = useState({})

  const { data, error: errorQuery, loading: loadingQuery } = useQuery(GET_RESTAURANTS)
  // The platform default a restaurant follows until it is given its own answer.
  const { data: configData } = useQuery(GET_QOR_CONFIG)
  const [mutate] = useMutation(UPDATE_COMMISSION)
  // Each restaurant's manual delivery fee, keyed by restaurant id.
  const { data: manualFeeData } = useQuery(MANUAL_DELIVERY_FEE_SETTINGS)
  const manualFees = new Map((manualFeeData?.manualDeliveryFeeSettings ?? []).map(s => [String(s.restaurantId), s]))

  const defaultTaxOwner = configData?.qorConfig?.platformKeepsTax === false ? RESTAURANT : PLATFORM
  // The platform's flat service fee, used for any restaurant without its own.
  const defaultServiceFee = configData?.qorConfig?.flatFee != null ? configData.qorConfig.flatFee : 0.99

  const serviceFeeOf = r => (r.serviceFeeOverride != null ? r.serviceFeeOverride : defaultServiceFee)

  // Restaurants without their own third-party % pay the standard.
  const { data: standardData } = useQuery(GET_STANDARD_FEES)
  const standardThirdParty = standardData?.merchantAgreementFees?.thirdPartyPercent ?? 5
  const thirdPartyOf = r => (r.thirdPartyPercent != null ? r.thirdPartyPercent : standardThirdParty)

  // What this restaurant's box shows: an unsaved change, else its own setting,
  // else the platform default.
  const taxOwnerOf = r => {
    if (taxOwners[r._id]) return taxOwners[r._id]
    if (r.platformKeepsTax == null) return defaultTaxOwner
    return r.platformKeepsTax ? PLATFORM : RESTAURANT
  }

  const showNotif = (id, type, msg) => {
    setNotifications(prev => ({ ...prev, [id]: { type, msg } }))
    setTimeout(() => setNotifications(prev => { const n = { ...prev }; delete n[id]; return n }), 3000)
  }

  const handleSave = (id, taxOwner, restaurant) => {
    const dEl = document.getElementById(`${id}-delivery`)
    const sEl = document.getElementById(`${id}-selfdelivery`)
    const pEl = document.getElementById(`${id}-pickup`)
    if (!dEl || !sEl || !pEl) return
    const deliveryCommissionRate = +dEl.value
    const selfDeliveryCommissionRate = +sEl.value
    const pickupCommissionRate = +pEl.value
    const rates = [deliveryCommissionRate, selfDeliveryCommissionRate, pickupCommissionRate]
    if (rates.some(v => v < 0)) {
      showNotif(id, 'error', t('The value Should not be in the negative'))
      return
    }
    if (rates.some(v => v > 100)) {
      showNotif(id, 'error', 'Rate cannot exceed 100%')
      return
    }
    const fEl = document.getElementById(`${id}-servicefee`)
    const serviceFee = fEl ? +fEl.value : null
    if (serviceFee == null || Number.isNaN(serviceFee) || serviceFee < 0 || serviceFee > 20) {
      showNotif(id, 'error', 'Service fee must be between $0 and $20')
      return
    }
    // Blank = back to the standard. A restaurant on the standard stays on it
    // (so a new standard reaches it) unless the box is changed.
    const tEl = document.getElementById(`${id}-thirdparty`)
    const tRaw = tEl ? tEl.value.trim() : ''
    const thirdPartyPercent = tRaw === '' ? null : +tRaw
    if (thirdPartyPercent != null && (Number.isNaN(thirdPartyPercent) || thirdPartyPercent < 0 || thirdPartyPercent > 100)) {
      showNotif(id, 'error', 'Third-party orders % must be between 0 and 100')
      return
    }
    const thirdPartyChanged = restaurant.thirdPartyPercent != null
      ? thirdPartyPercent !== restaurant.thirdPartyPercent
      : thirdPartyPercent != null && thirdPartyPercent !== standardThirdParty
    mutate({
      variables: {
        id,
        deliveryCommissionRate,
        selfDeliveryCommissionRate,
        pickupCommissionRate,
        platformKeepsTax: taxOwner === PLATFORM,
        serviceFee,
        ...(thirdPartyChanged ? { thirdPartyPercent } : {})
      },
      onCompleted: () => showNotif(id, 'success', t('UpdateSuccessful')),
      onError: () => showNotif(id, 'error', t('Update Error'))
    })
  }

  // Show the existing value: prefer the per-scenario rate, else fall back the
  // same way the split engine does (self-delivery → delivery → legacy single
  // commissionRate) so the boxes show what the restaurant would actually pay.
  const initialRate = (r, kind) => {
    let v
    if (kind === 'pickup') v = r.pickupCommissionRate
    else if (kind === 'selfdelivery') v = r.selfDeliveryCommissionRate != null ? r.selfDeliveryCommissionRate : r.deliveryCommissionRate
    else v = r.deliveryCommissionRate
    if (v != null) return v
    return r.commissionRate != null ? r.commissionRate : 0
  }

  const restaurantList = data?.restaurants ?? []

  return (
    <>
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1e40af 100%)', px: { xs: 2, md: 4 }, pt: 3.5, pb: 5, position: 'relative', overflow: 'hidden', '&::before': { content: '""', position: 'absolute', top: -60, right: -60, width: 260, height: 260, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' } }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: '14px', background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PercentIcon sx={{ color: '#93c5fd', fontSize: 26 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#fff', lineHeight: 1.15, letterSpacing: '-0.02em' }}>Commission Rates</Typography>
            <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', mt: 0.3 }}>Set per-restaurant commission % — separate rates for when we deliver, when the restaurant delivers, and pickup — and who keeps the sales tax</Typography>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 3, pb: 4 }}>
        <Box display="flex" gap={2} mb={3} flexWrap="wrap">
          <Paper elevation={0} sx={{ background: 'linear-gradient(135deg,#eff6ff,#dbeafe)', border: '1px solid #bfdbfe', borderRadius: '16px', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', minWidth: 160 }}>
            <Box sx={{ width: 44, height: 44, borderRadius: '12px', background: '#3b82f618', border: '1px solid #3b82f630', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b82f6', flexShrink: 0 }}><StorefrontIcon sx={{ fontSize: 22 }} /></Box>
            <Box>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>{restaurantList.length}</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500, mt: 0.2 }}>Restaurants</Typography>
            </Box>
          </Paper>
        </Box>

        <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
          <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid #f1f5f9', background: '#fafbfc' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>Restaurant Commission Rates</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mt: 0.3 }}>Enter the commission percentage for each restaurant, its service fee, and who keeps the sales tax on its orders</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mt: 0.5 }}>
              Whoever keeps the tax remits it to the state — set it to Restaurant where the restaurant files its own sales tax.
              The customer pays the same tax either way, and orders already placed keep the answer they were sold with.
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mt: 0.5 }}>
              The service fee is what the customer pays on top of a card order, and RushmyFood keeps it. Checkout shows this
              restaurant&apos;s fee, so the line and the charge always match. Blank rows start from the platform default in Configuration.
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mt: 0.5 }}>
              Third-party orders: the % of the food subtotal charged when our drivers deliver a DoorDash, Uber Eats or Grubhub
              order, on top of the delivery fee and tip. Leave it blank to follow the standard in Signup Standards ({standardThirdParty}%); 0 charges nothing.
            </Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            {errorQuery && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>Error: {errorQuery.message}</Alert>}
            {loadingQuery ? <CustomLoader /> : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {restaurantList.map(restaurant => (
                  <Box key={restaurant._id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: '12px', border: '1px solid #f1f5f9', bgcolor: '#fafbfc', flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 200 }}>
                      <Box sx={{ width: 36, height: 36, borderRadius: '9px', bgcolor: '#eff6ff', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <StorefrontIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
                      </Box>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1e293b' }}>{restaurant.name}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, flexWrap: 'wrap' }}>
                      {[
                        { kind: 'delivery', suffix: 'delivery', label: 'We deliver' },
                        { kind: 'selfdelivery', suffix: 'selfdelivery', label: 'Restaurant delivers' },
                        { kind: 'pickup', suffix: 'pickup', label: 'Pickup' },
                      ].map(({ kind, suffix, label }) => (
                        <Box key={suffix}>
                          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', mb: 0.4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</Typography>
                          <TextField
                            id={`${restaurant._id}-${suffix}`}
                            type="number"
                            size="small"
                            defaultValue={initialRate(restaurant, kind)}
                            inputProps={{ min: 0, max: 100, step: 1 }}
                            InputProps={{
                              endAdornment: <InputAdornment position="end"><PercentIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment>,
                              sx: { borderRadius: '10px', fontSize: '0.9rem', bgcolor: '#fff', width: 120 }
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#e2e8f0' }, '&:hover fieldset': { borderColor: '#93c5fd' }, '&.Mui-focused fieldset': { borderColor: '#3b82f6' } } }}
                          />
                        </Box>
                      ))}
                      <Box>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', mb: 0.4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Service fee</Typography>
                        <TextField
                          id={`${restaurant._id}-servicefee`}
                          type="number"
                          size="small"
                          defaultValue={serviceFeeOf(restaurant)}
                          inputProps={{ min: 0, max: 20, step: 0.1 }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><Typography sx={{ fontSize: '0.85rem', color: '#94a3b8' }}>$</Typography></InputAdornment>,
                            sx: { borderRadius: '10px', fontSize: '0.9rem', bgcolor: '#fff', width: 120 }
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#e2e8f0' }, '&:hover fieldset': { borderColor: '#93c5fd' }, '&.Mui-focused fieldset': { borderColor: '#3b82f6' } } }}
                        />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', mb: 0.4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Third-party orders</Typography>
                        <TextField
                          // Re-mounts when the standard loads, so the box shows it.
                          key={`${restaurant._id}-thirdparty-${standardThirdParty}`}
                          id={`${restaurant._id}-thirdparty`}
                          type="number"
                          size="small"
                          defaultValue={thirdPartyOf(restaurant)}
                          placeholder={String(standardThirdParty)}
                          inputProps={{ min: 0, max: 100, step: 0.5 }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end"><PercentIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment>,
                            sx: { borderRadius: '10px', fontSize: '0.9rem', bgcolor: '#fff', width: 120 }
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#e2e8f0' }, '&:hover fieldset': { borderColor: '#93c5fd' }, '&.Mui-focused fieldset': { borderColor: '#3b82f6' } } }}
                        />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', mb: 0.4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Who keeps tax</Typography>
                        <TextField
                          select
                          size="small"
                          value={taxOwnerOf(restaurant)}
                          onChange={e => setTaxOwners(prev => ({ ...prev, [restaurant._id]: e.target.value }))}
                          InputProps={{ sx: { borderRadius: '10px', fontSize: '0.9rem', bgcolor: '#fff', width: 170 } }}
                          sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#e2e8f0' }, '&:hover fieldset': { borderColor: '#93c5fd' }, '&.Mui-focused fieldset': { borderColor: '#3b82f6' } } }}
                        >
                          <MenuItem value={PLATFORM}>RushmyFood</MenuItem>
                          <MenuItem value={RESTAURANT}>Restaurant</MenuItem>
                        </TextField>
                      </Box>
                      <Button variant="contained" size="small" startIcon={<SaveIcon sx={{ fontSize: 15 }} />}
                        onClick={() => handleSave(restaurant._id, taxOwnerOf(restaurant), restaurant)}
                        sx={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)', borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 2.5, py: 0.9, fontSize: '0.8rem', boxShadow: '0 3px 10px rgba(59,130,246,0.35)', '&:hover': { background: 'linear-gradient(135deg,#2563eb,#1d4ed8)' } }}>
                        Save
                      </Button>
                    </Box>
                    <ManualDeliveryFeeRow
                      restaurantId={restaurant._id}
                      setting={manualFees.get(String(restaurant._id))}
                      onNotify={showNotif}
                    />
                    {notifications[restaurant._id] && (
                      <Alert severity={notifications[restaurant._id].type} sx={{ py: 0, px: 1.5, borderRadius: '8px', fontSize: '0.75rem', width: '100%' }}>
                        {notifications[restaurant._id].msg}
                      </Alert>
                    )}
                  </Box>
                ))}
                {restaurantList.length === 0 && (
                  <Box py={6} textAlign="center">
                    <StorefrontIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                    <Typography sx={{ color: '#64748b' }}>No restaurants found</Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      </Container>
    </>
  )
}

export default withTranslation()(Commission)